import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { authService } from '@services/auth.service';
import { useAuthStore } from '@store/authStore';
import { setAccessToken } from '@lib/axios';
import { queryClient } from '@lib/queryClient';
import { queryKeys } from '@constants/queryKeys';
import { ROUTES } from '@constants/routes';

interface LoginPayload {
  mainCentreId: string;
  centreId: string;
  email: string;
  password: string;
}

export const useAuth = () => {
  const { user, isAuthenticated, setAuth, clearAuth } = useAuthStore();
  const navigate = useNavigate();

  const loginMutation = useMutation({
    mutationFn: ({ email, password, centreId }: LoginPayload) =>
      authService.login({ email, password, centreId }),
    onSuccess: async ({ data }, variables) => {
      setAccessToken(data.accessToken);
      setAuth(data.user);
      // Wipe any cache carried over from a previous login in this same tab.
      // Logout/login navigate client-side (no page reload), so React Query's
      // cache survives the boundary — and only the dashboard key is scoped by
      // session/centre, so admissions/installments/users/reports would otherwise
      // be served under identical keys and show the PREVIOUS session's records
      // even though the top-bar chip already reflects the new one.
      queryClient.clear();
      // Both are persisted: every scoped request sends the main centre too, and
      // the top bar restores this pair on the next boot.
      localStorage.setItem('mainCentreId', variables.mainCentreId);
      localStorage.setItem('centreId', variables.centreId);
      try {
        const { data: sessionData } = await authService.getCurrentSession();
        localStorage.setItem('sessionId', sessionData.session._id);
      } catch {
        // non-fatal — session header just won't be sent until next login
      }
      navigate(ROUTES.DASHBOARD);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authService.logout(),
    onSettled: () => {
      setAccessToken(null);
      clearAuth();
      localStorage.removeItem('sessionId');
      // Drop every cached query so the next login starts on a clean slate and
      // can't inherit this session's/user's records (see the login handler).
      queryClient.clear();
      navigate(ROUTES.LOGIN);
    },
  });

  return {
    user,
    isAuthenticated,
    login: loginMutation.mutate,
    loginAsync: loginMutation.mutateAsync,
    logout: logoutMutation.mutate,
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    loginError: loginMutation.error,
  };
};

export const useCurrentUser = () =>
  useQuery({
    queryKey: queryKeys.auth.me(),
    queryFn: () => authService.me().then((r) => r.data.user),
    enabled: useAuthStore.getState().isAuthenticated,
  });

export const useForgotPassword = () => {
  const mutation = useMutation({
    mutationFn: (email: string) => authService.forgotPassword(email),
  });
  return {
    forgotPassword: mutation.mutate,
    isSending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    forgotPasswordError: mutation.error,
  };
};

export const useResetPassword = () => {
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authService.resetPassword(token, password),
    onSuccess: () => {
      navigate(ROUTES.LOGIN);
    },
  });
  return {
    resetPassword: mutation.mutate,
    isResetting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    resetPasswordError: mutation.error,
  };
};
